
import { ProductUnitEnum } from '../constant';
import { Product } from '../types';
import { APIResponse } from '@/interfaces';
import { Variation } from '@/features/seller';
import { baseSplitApi } from '@/services';
import { ProductForm } from '../validation';

interface ProductUnit {
  value: ProductUnitEnum;
  viewValue: Record<string, string>;
}
export const productApi = baseSplitApi.injectEndpoints({
  endpoints: builder => ({
    getProductList: builder.query<Product[], Record<string, any>>({
      query: args => {
        const url = `/v1/product?${new URLSearchParams(args)}`;
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Product' as const, id: _id })),
              { type: 'ProductList', id: 'LIST' },
            ]
          : [{ type: 'ProductList', id: 'LIST' }],
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page == 1) {
          currentCache.length = 0;
          currentCache.push(...newItems);
        } else {
          currentCache.push(...newItems);
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      transformErrorResponse: (response: { status: string | number }) =>
        response.status,
      transformResponse: (response: { data: Product[] }) => {
        return response.data;
      },
    }),
    getProduct: builder.query<Product, Record<string, any>>({
      query: ({ productId, ...params }) => {
        return {
          url: `/v1/product/` + productId,
          method: 'GET',
          params
        };
      },
      providesTags: (result, error, { productId }) => [
        { type: 'Product', id: productId }
      ],
      transformErrorResponse: (response: { status: string | number }) =>
        response.status,
      transformResponse: (response: { data: Product }) => response.data,
    }),
    getProductByBarcode: builder.query<
      APIResponse<Product>,
      Record<string, any>
    >({
      query: ({ barcode }) => ({
        url: `/v1/seller/store/product/barcode/${barcode}`,
        method: 'GET',
      }),
    }),
    getStoreProductCount: builder.query<APIResponse<number>, null>({
      query: () => ({
        url: `/v1/seller/store/product/count`,
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      }),
    }),
    createProduct: builder.mutation<APIResponse<Product>, Partial<Product>>({
      query: body => {
        return {
          url: '/v1/product',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [{ type: 'ProductList', id: 'LIST' }, 'StockValue', 'LowStockCount'],
    }),
    updateProduct: builder.mutation<APIResponse<Product>, Partial<ProductForm & { sellerProductDetailId?: string }>>({
      query: ({ _id, ...body }) => {
        return {
          url: '/v1/product/' + _id + "/new",
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: (result, error, { _id }) => [
        { type: 'ProductList', id: 'LIST' },
        { type: 'Product', id: _id },
        'StockValue',
        'LowStockCount'
      ],
    }),
    addProduct: builder.mutation<
      APIResponse<Product>,
      { productId: string; body: Partial<Variation>[] }
    >({
      query: ({ productId, body }) => {
        return {
          url: `/v1/seller/product/${productId}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [{ type: 'ProductList', id: 'LIST' }, 'StockValue', 'LowStockCount'],
    }),
    getProductUnitList: builder.query<ProductUnit[], any>({
      query: () => ({
        url: `/v1/product/unit`,
        method: 'GET',
      }),
      transformErrorResponse: (response: { status: string | number }) =>
        response.status,
      transformResponse: (response: { data: ProductUnit[] }) => response.data,
    }),
    getCreateProductList: builder.query<Product[], Record<string, any>>({
      query: params => ({
        url: '/v1/product',
        method: 'GET',
        params,
      }),
      // serializeQueryArgs: ({endpointName}) => {
      //   return endpointName;
      // },
      // merge: (currentCache, newItems, {arg}) => {
      //   if (arg.page == 1) {
      //     currentCache.length = 0;
      //     currentCache.push(...newItems);
      //   } else {
      //     currentCache.push(...newItems);
      //   }
      // },
      // forceRefetch({currentArg, previousArg}) {
      //   return !isEqual(currentArg, previousArg);
      // },
      // providesTags: (result, error, args) => {},
      transformErrorResponse: (response: { status: string | number }) =>
        response.status,
      transformResponse: (response: { data: Product[] }) => response.data,
    }),
  }),
});

export const {
  useLazyGetProductListQuery,
  useGetProductListQuery,
  useGetProductQuery,
  useLazyGetProductQuery,
  useLazyGetProductByBarcodeQuery,
  useCreateProductMutation,
  useGetProductUnitListQuery,
  useAddProductMutation,
  useGetCreateProductListQuery,
  useUpdateProductMutation,
  useGetStoreProductCountQuery,
  useLazyGetStoreProductCountQuery
} = productApi;
